/**
 * mailApproveApi.js
 * API calls for the manager Submission Approval (mail-approve) flow.
 *
 * EM-Testing owns this service itself — @realtek/core-theme intentionally does
 * not ship feature-specific business logic, only the shared building blocks
 * (SUBMISSIONS_URL, getStoredToken, logout). See CORE-THEME's src/index.js:
 * "The host app keeps those thin route wrappers itself and composes them from
 * the building blocks." This mirrors ZINNEXT-V1's local services/submissionsApi.js,
 * but reuses the npm package for auth/token/base-url instead of duplicating them.
 *
 * Endpoints (SUBMISSIONS_URL):
 *   GET  /review?tenantId=&businessId=&businessUnitId=&submissionId=  — fetch data
 *   GET  /evaluate-form?submissionId=&proposedRate=35 $ USD Hourly    — approve + set rate
 *   GET  /approved?submissionId=                                       — approve without rate
 *   POST /reject-submission                                            — reject
 */

import { SUBMISSIONS_URL } from './apiConfig';
import { getStoredToken, logout } from './authApi';

async function fetchJsonWithAuth(path, options = {}) {
  const token = getStoredToken();
  if (!token) {
    const error = new Error('Authentication required');
    error.status = 401;
    throw error;
  }

  const res = await fetch(`${SUBMISSIONS_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });

  if (res.status === 401) logout();

  if (!res.ok) {
    const error = new Error(`API ${res.status}: ${res.statusText}`);
    error.status = res.status;
    throw error;
  }
  return res.json();
}

/**
 * Fetch submission data for the Evaluate Form page.
 * Uses /review — returns the full record without modifying any status.
 */
export async function getEvaluateForm(submissionId) {
  const tenantId       = localStorage.getItem('tenantId')       || '';
  const businessId     = localStorage.getItem('businessId')     || '';
  const businessUnitId = localStorage.getItem('businessUnitId') || '';

  const params = new URLSearchParams({ tenantId, businessId, businessUnitId, submissionId });
  const json = await fetchJsonWithAuth(`/review?${params.toString()}`);
  return json.data ?? json;
}

/**
 * Manager approves the submission.
 * Rate string format the backend expects: "35 $ USD Hourly"
 */
export async function approveSubmission(submissionId, { proposedRate, currency, payUnit } = {}) {
  const hasRate = proposedRate !== undefined && proposedRate !== '' && proposedRate !== null;

  if (hasRate) {
    const sym = { USD: '$', INR: '₹', EUR: '€', GBP: '£', CAD: '$' }[currency] ?? '$';
    const rateString = `${proposedRate} ${sym} ${currency ?? 'USD'} ${payUnit ?? 'Hourly'}`;
    const json = await fetchJsonWithAuth(
      `/evaluate-form?submissionId=${encodeURIComponent(submissionId)}&proposedRate=${encodeURIComponent(rateString)}`
    );
    return json.data ?? json;
  }

  const json = await fetchJsonWithAuth(`/approved?submissionId=${encodeURIComponent(submissionId)}`);
  return json.data ?? json;
}

export async function approveSubmissionV2(submissionId, payload = {}) {
  const json = await fetchJsonWithAuth(`/approved/v2?submissionId=${encodeURIComponent(submissionId)}`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return json.data ?? json;
}

export async function getApproveMailDraft(submissionId) {
  // Manager-approve mail must be addressed to the internal approver, not the
  // client's recruiter/contact. The submissions service keeps the same client
  // mail template/body for this mode and only changes recipient resolution.
  const params = new URLSearchParams({ submissionId, submissionType: 'manager' });
  const json = await fetchJsonWithAuth(`/mail-form?${params.toString()}`);
  return json.data ?? json;
}

export async function sendApproveMail(payload) {
  const token = getStoredToken();
  if (!token) {
    const error = new Error('Authentication required');
    error.status = 401;
    throw error;
  }

  const formData = new FormData();
  formData.append('json', JSON.stringify(payload));

  const res = await fetch(`${SUBMISSIONS_URL}/send-mail-client-doc`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  if (res.status === 401) logout();

  const json = await res.json().catch(() => ({}));
  if (!res.ok || json?.status === false) {
    const error = new Error(json?.message ?? json?.error ?? `API ${res.status}: ${res.statusText}`);
    error.status = res.status;
    throw error;
  }
  return json.data ?? json;
}

/**
 * Manager rejects the submission.
 * rejectReasonId: 1 = re-submission, 2 = rejected, 3 = need update
 */
export async function rejectSubmission(submissionId, { rejectReasonId, rejectReasonComments }) {
  const json = await fetchJsonWithAuth('/reject-submission', {
    method: 'POST',
    body: JSON.stringify({ submissionId, rejectReasonId, rejectReasonComments }),
  });
  return json.data ?? json;
}
